/**
 * Asynchronous card imaging pipeline (client orchestrator).
 *
 * Flow for every uploaded photo — identical for the sell flow, re-shoots and
 * any future upload surface:
 *
 *   1. `uploading`  RAW original → private `card-originals` (never re-encoded)
 *                   + a fast display copy → `card-images`, and a `card_assets`
 *                   row so the photo is usable in a listing immediately.
 *   2. `processing` the studio pipeline runs in a Web Worker: detect → correct
 *                   perspective → upright → crop → centre → normalise scale →
 *                   remove background/desk/hands/shadows → premium dark
 *                   backdrop → soft studio shadow → derivative ladder.
 *   3. `ready`      deep-zoom master, ultra HD, preview and thumbnail are
 *                   uploaded and the asset flips to ready. The listing URL is
 *                   unchanged, so every surface switches to the processed
 *                   render on its next request.
 *
 * Nothing here blocks publishing: a listing can go live during step 2.
 */

import { supabase } from "@/integrations/supabase/client";
import { verifyCardImageUpload } from "@/lib/card-image-verify.functions";
import { SITE_URL } from "@/lib/seo";
import { uploadBlobWithRetry } from "@/lib/storage-upload";
import { ctx2d, encodeCanvas, makeCanvas } from "./canvas";
import { BUCKET_DERIVATIVES, BUCKET_ORIGINALS, SIZE_PREVIEW } from "./constants";
import { decodeImageFile } from "./decode";
import {
  completeCardAsset,
  createCardAsset,
  failCardAsset,
  type CardAssetStatus,
} from "./assets.functions";
import { highQualityDownscale } from "./resize";
import { runStudio } from "./run";
import type { StudioOptions, StudioResult, StudioStage } from "./studio";

export type CardAssetVariant = "thumb" | "preview" | "ultra" | "deepzoom" | "source";

/** Stable, listing-safe URL for an asset. Resolves to the best rendition. */
export function cardAssetUrl(assetId: string, variant: CardAssetVariant = "preview"): string {
  return `${SITE_URL}/api/public/card-image/${assetId}?v=${variant}`;
}

/** Extract the asset id from a stable card image URL, if it is one. */
export function cardAssetIdFromUrl(url: string): string | null {
  const m = /\/api\/public\/card-image\/([0-9a-f-]{36})/i.exec(url);
  return m ? m[1]! : null;
}

export type PipelineProgress = {
  status: CardAssetStatus;
  /** Sub-step of the studio pipeline while status is `processing`. */
  stage: StudioStage | null;
  /** 0–1 across the whole upload + processing lifecycle. */
  progress: number;
};

export type CardAssetHandle = {
  assetId: string;
  /** Immutable derivative generation currently referenced by every surface. */
  revision: number;
  /** Stable URL to store on the listing. */
  url: string;
  thumbUrl: string;
  originalPath: string;
  sourcePath: string;
};

function extensionFor(file: File): string {
  const n = file.name.toLowerCase();
  const dot = n.lastIndexOf(".");
  const ext = dot >= 0 ? n.slice(dot + 1) : "";
  if (/^[a-z0-9]{2,5}$/.test(ext)) return ext;
  const t = (file.type || "").toLowerCase();
  if (t === "image/png") return "png";
  if (t === "image/webp") return "webp";
  if (t === "image/heic" || t === "image/heif") return "heic";
  return "jpg";
}

/**
 * A quick, honest copy of what the seller uploaded — orientation-corrected and
 * size-capped only. This is what the listing shows until processing finishes.
 */
async function buildDisplayCopy(file: File): Promise<{ blob: Blob; w: number; h: number }> {
  const decoded = await decodeImageFile(file, { workingMaxEdge: 2000 });
  const k = Math.min(1, SIZE_PREVIEW / Math.max(decoded.width, decoded.height));
  const w = Math.max(1, Math.round(decoded.width * k));
  const h = Math.max(1, Math.round(decoded.height * k));
  const scaled =
    k < 1 ? highQualityDownscale(decoded.canvas, w, h) : decoded.canvas;
  // JPEG keeps this step fast and universally decodable on older Safari.
  const flat = makeCanvas(w, h);
  const fctx = ctx2d(flat);
  fctx.fillStyle = "#0a0b0d";
  fctx.fillRect(0, 0, w, h);
  fctx.drawImage(scaled as CanvasImageSource, 0, 0, w, h);
  const blob = await encodeCanvas(flat, "image/jpeg", 0.86);
  return { blob, w, h };
}

/**
 * Step 1 + 2 kicked off together. Resolves as soon as the photo is usable
 * (status `processing`); the studio work continues in the background and
 * reports through `onProgress`.
 */
export async function startCardAssetPipeline(
  userId: string,
  file: File,
  onProgress?: (p: PipelineProgress) => void,
  options: StudioOptions = {},
): Promise<{ handle: CardAssetHandle; processed: Promise<StudioResult | null> }> {
  const assetId = crypto.randomUUID();
  const revision = 0;
  const dir = `${userId}/${assetId}`;
  const originalPath = `${dir}/original.${extensionFor(file)}`;
  const sourcePath = `${dir}/r${revision}/source.jpg`;

  onProgress?.({ status: "uploading", stage: null, progress: 0.05 });

  const display = await buildDisplayCopy(file);
  await uploadBlobWithRetry(BUCKET_DERIVATIVES, sourcePath, display.blob, {
    contentType: "image/jpeg",
  });
  await verifyCardImageUpload({ data: { path: sourcePath } });
  onProgress?.({ status: "uploading", stage: null, progress: 0.25 });

  // RAW original: byte-for-byte, private bucket, never re-encoded.
  await uploadBlobWithRetry(BUCKET_ORIGINALS, originalPath, file, {
    contentType: file.type || "application/octet-stream",
  });
  onProgress?.({ status: "uploading", stage: null, progress: 0.4 });

  await createCardAsset({
    data: {
      id: assetId,
      originalPath,
      sourcePath,
      mime: file.type || "image/jpeg",
      width: display.w,
      height: display.h,
    },
  });

  const handle: CardAssetHandle = {
    assetId,
    revision,
    url: `${cardAssetUrl(assetId, "preview")}&rev=${revision}`,
    thumbUrl: `${cardAssetUrl(assetId, "thumb")}&rev=${revision}`,
    originalPath,
    sourcePath,
  };

  onProgress?.({ status: "processing", stage: "decoding", progress: 0.45 });

  const processed = processInBackground(userId, assetId, revision, file, onProgress, options);
  return { handle, processed };
}

/**
 * Re-run the studio render for an asset that already exists — used by the
 * manual crop editor and the "Retry AI" recovery action. The RAW original and
 * the seller's uploaded copy are untouched; only derivatives are replaced.
 */
export function reprocessCardAsset(
  userId: string,
  assetId: string,
  revision: number,
  processingFile: File,
  canonicalFile: File,
  options: StudioOptions = {},
  onProgress?: (p: PipelineProgress) => void,
): Promise<StudioResult | null> {
  return processInBackground(userId, assetId, revision, processingFile, onProgress, options, canonicalFile);
}

const STAGE_PROGRESS: Record<StudioStage, number> = {
  decoding: 0.5,
  detecting: 0.58,
  rectifying: 0.68,
  encoding: 0.8,
  done: 0.86,
};


async function processInBackground(
  userId: string,
  assetId: string,
  revision: number,
  file: File,
  onProgress?: (p: PipelineProgress) => void,
  options: StudioOptions = {},
  canonicalFile?: File,
): Promise<StudioResult | null> {
  const dir = `${userId}/${assetId}/r${revision}`;
  try {
    // Every edit gets an immutable, orientation-normalised display source. The
    // public endpoint and every derivative switch to this revision together.
    if (canonicalFile) {
      const display = await buildDisplayCopy(canonicalFile);
      const sourcePath = `${dir}/source.jpg`;
      await uploadBlobWithRetry(BUCKET_DERIVATIVES, sourcePath, display.blob, {
        contentType: "image/jpeg",
      });
      await verifyCardImageUpload({ data: { path: sourcePath } });
    }
    const result = await runStudio(file, options, (stage) =>
      onProgress?.({ status: "processing", stage, progress: STAGE_PROGRESS[stage] ?? 0.6 }),
    );

    const { deepzoom, ultra, preview, thumb, mime, ext } = result.derivatives;
    const paths = {
      deepzoomPath: `${dir}/deepzoom.${ext}`,
      ultraPath: `${dir}/ultra.${ext}`,
      previewPath: `${dir}/preview.${ext}`,
      thumbPath: `${dir}/thumb.${ext}`,
    };

    const uploads: [string, Blob][] = [
      [paths.deepzoomPath, deepzoom],
      [paths.ultraPath, ultra],
      [paths.previewPath, preview],
      [paths.thumbPath, thumb],
    ];
    let done = 0;
    for (const [path, blob] of uploads) {
      await uploadBlobWithRetry(BUCKET_DERIVATIVES, path, blob, { contentType: mime });
      await verifyCardImageUpload({ data: { path } });
      done += 1;
      onProgress?.({
        status: "processing",
        stage: "encoding",
        progress: 0.86 + (done / uploads.length) * 0.13,
      });
    }




    await completeCardAsset({
      data: {
        id: assetId,
        ...paths,
        sourcePath: `${dir}/source.jpg`,
        mime,
        meta: { ...result.meta, revision },
      },
    });
    onProgress?.({ status: "ready", stage: "done", progress: 1 });
    return result;
  } catch (err) {
    const message = err instanceof Error ? err.message : "Processing failed";
    try {
      await failCardAsset({ data: { id: assetId, error: message } });
    } catch {
      /* the asset stays on the uploaded copy either way */
    }
    onProgress?.({ status: "failed", stage: null, progress: 1 });
    return null;
  }
}

/** Remove every object belonging to an asset (used when a draft is discarded). */
export async function discardCardAsset(handle: CardAssetHandle): Promise<void> {
  const dir = handle.sourcePath.slice(0, handle.sourcePath.lastIndexOf("/"));
  try {
    // The derivative extension depends on codec support (webp or jpg), so
    // list the folder rather than guessing file names.
    const { data } = await supabase.storage.from(BUCKET_DERIVATIVES).list(dir);
    const paths = (data ?? []).map((o) => `${dir}/${o.name}`);
    if (!paths.includes(handle.sourcePath)) paths.push(handle.sourcePath);
    await supabase.storage.from(BUCKET_DERIVATIVES).remove(paths);
    await supabase.storage.from(BUCKET_ORIGINALS).remove([handle.originalPath]);
  } catch {
    /* cleanup is advisory */
  }
}

